import { apiRequest } from '@/utils/api-request';

// Capitol MCP Gateway registry (ENG-5304). Management endpoints are
// org-admin-only server-side (require_org_admin); this page is additionally
// route-guarded by OwnerOrAdminRoute.

export interface McpToolDef {
  kind: 'workflow' | 'agent' | 'collection' | 'graph' | 'snapshot';
  tool_name: string;
  name?: string;
  description?: string;
  workflow_id?: string;
  version_id?: string | null;
  agent_id?: string;
  asset_id?: string;
  input_schema?: Record<string, unknown>;
}

export interface McpServerKey {
  key_id: string;
  label: string;
  created_at?: string;
  created_by?: string;
  revoked_at?: string | null;
}

export interface McpServerSwitches {
  runs_enabled: boolean;
  writes_enabled: boolean;
  rate_cap_per_minute: number;
  result_max_chars: number;
}

export interface McpServer {
  server_id: string;
  slug: string;
  name: string;
  description?: string;
  server_type: 'curated' | 'generic';
  status: 'active' | 'disabled' | 'deleted';
  endpoint: string;
  tool_count: number;
  tools?: McpToolDef[];
  switches?: McpServerSwitches;
  keys?: McpServerKey[];
  origin?: string;
  created_by_email?: string;
  created_at?: string;
  updated_at?: string;
}

export interface McpSelections {
  workflows?: { workflow_id: string; version_id?: string | null }[];
  agents?: { agent_id: string }[];
  data_assets?: {
    asset_type: 'collection' | 'graph' | 'snapshot';
    asset_id: string;
  }[];
}

export interface McpAuditEvent {
  event_id: string;
  ts: string;
  tool: string;
  outcome: string;
  caller?: string;
  duration_ms?: number;
  detail?: string;
}

export const listMcpServers = (orgId: string) =>
  apiRequest<{ servers: McpServer[] }>({
    url: `/mcp-servers/${orgId}`,
    method: 'GET',
    target: 'platform',
  });

// Idempotent: creates the org's generic gateway on first call, returns the
// existing one afterwards (and repairs its serving projection). Org-scoped
// service identity — per-user scoping arrives with ENG-5281.
export const provisionGenericMcpServer = (orgId: string) =>
  apiRequest<{ server: McpServer; created: boolean }>({
    url: `/mcp-servers/${orgId}/provision-generic`,
    method: 'POST',
    target: 'platform',
  });

export const previewMcpServer = (orgId: string, selections: McpSelections) =>
  apiRequest<{ tools: McpToolDef[]; unresolved: unknown[] }>({
    url: `/mcp-servers/${orgId}/preview`,
    method: 'POST',
    target: 'platform',
    body: selections as Record<string, unknown>,
  });

export const createMcpServer = (
  orgId: string,
  payload: McpSelections & {
    name: string;
    slug?: string;
    description?: string;
    origin?: string;
  },
) =>
  apiRequest<{ server: McpServer; api_key?: string; api_key_note?: string }>({
    url: `/mcp-servers/${orgId}`,
    method: 'POST',
    target: 'platform',
    body: { origin: 'ui', ...payload } as Record<string, unknown>,
  });

export const getMcpServer = (orgId: string, serverId: string) =>
  apiRequest<{ server: McpServer }>({
    url: `/mcp-servers/${orgId}/${serverId}`,
    method: 'GET',
    target: 'platform',
  });

export const updateMcpServer = (
  orgId: string,
  serverId: string,
  payload: Partial<McpSelections> & {
    name?: string;
    description?: string;
    status?: 'active' | 'disabled';
  },
) =>
  apiRequest<{ server: McpServer; warnings: string[] }>({
    url: `/mcp-servers/${orgId}/${serverId}`,
    method: 'PATCH',
    target: 'platform',
    body: payload as Record<string, unknown>,
  });

export const deleteMcpServer = (orgId: string, serverId: string) =>
  apiRequest<{ deleted: boolean; warnings: string[] }>({
    url: `/mcp-servers/${orgId}/${serverId}`,
    method: 'DELETE',
    target: 'platform',
  });

export const mintMcpServerKey = (
  orgId: string,
  serverId: string,
  label: string,
) =>
  apiRequest<{ api_key: string; key_id: string; label: string; note: string }>({
    url: `/mcp-servers/${orgId}/${serverId}/keys`,
    method: 'POST',
    target: 'platform',
    body: { label },
  });

export const revokeMcpServerKey = (
  orgId: string,
  serverId: string,
  keyId: string,
) =>
  apiRequest<{ revoked: boolean }>({
    url: `/mcp-servers/${orgId}/${serverId}/keys/${keyId}/revoke`,
    method: 'POST',
    target: 'platform',
  });

export const getMcpServerDocs = (orgId: string, serverId: string) =>
  apiRequest<{
    markdown: string;
    endpoint: string;
    snippets: Record<string, unknown>;
  }>({
    url: `/mcp-servers/${orgId}/${serverId}/docs`,
    method: 'GET',
    target: 'platform',
  });

export const getMcpServerAudit = (orgId: string, serverId: string) =>
  apiRequest<{ events: McpAuditEvent[] }>({
    url: `/mcp-servers/${orgId}/${serverId}/audit`,
    method: 'GET',
    target: 'platform',
  });

// ── Resource catalogs for the create wizard ────────────────────────────────

export interface WizardWorkflow {
  id: string;
  name: string;
  description?: string;
}

export const listWizardWorkflows = async (
  orgId: string,
): Promise<WizardWorkflow[]> => {
  const response = await apiRequest<{
    workflows: { id: string; name: string; description?: string }[];
  }>({
    url: `/api/v1/orgs/${orgId}/workflows`,
    method: 'GET',
    target: 'workflow',
  });
  return response.data?.workflows || [];
};

export interface WizardAsset {
  asset_type: 'collection' | 'graph';
  asset_id: string;
  name: string;
}

export const listWizardAssets = async (
  orgId: string,
): Promise<WizardAsset[]> => {
  const assets: WizardAsset[] = [];
  const collections = await apiRequest<
    | { collections: { id: string; name: string; data_type?: string }[] }
    | { id: string; name: string; data_type?: string }[]
  >({
    url: `/collections/${orgId}`,
    method: 'GET',
    target: 'platform',
  });
  const collectionList = Array.isArray(collections.data)
    ? collections.data
    : collections.data?.collections || [];
  for (const collection of collectionList) {
    if (!collection?.id) continue;
    assets.push({
      asset_type: 'collection',
      asset_id: collection.id,
      name: collection.name || collection.id,
    });
  }
  try {
    const graphs = await apiRequest<
      | { graphs: { id?: string; graph_id?: string; name?: string }[] }
      | { id?: string; graph_id?: string; name?: string }[]
    >({
      url: `/context-graphs/${orgId}`,
      method: 'GET',
      target: 'platform',
      suppressSentryStatuses: [404],
    });
    const graphList = Array.isArray(graphs.data)
      ? graphs.data
      : graphs.data?.graphs || [];
    for (const graph of graphList) {
      const id = graph.id || graph.graph_id;
      if (!id) continue;
      assets.push({
        asset_type: 'graph',
        asset_id: id,
        name: graph.name || id,
      });
    }
  } catch {
    // graphs are optional — collections alone are fine for the picker
  }
  return assets;
};
